// Results shell — summary stats, The Crux panel, the Side-by-side /
// Unified / Raw view switcher, and the diff surface with the ribbon layer.
// V1 share/export options only: the encrypted-link (V3) and Scrub-PII (V2)
// share rows, and the PNG / Merged / Embed (V2/V3) export items, are omitted.
// Populated by the engine from Phase 3 onward; hidden until Compare runs.
export function results() {
  return `
  <section class="results" id="results" aria-labelledby="resultsHeading">
    <h2 class="sr-only" id="resultsHeading">Comparison results</h2>
    <!-- Shown only when a comparison is actually slow (COMPARE_LOADER_DELAY in
         main.js). A spinner that appears and vanishes inside 40ms is noise, and
         most comparisons finish in far less than that, so the usual run produces
         no loader at all. While it IS up the previous result stays on screen,
         dimmed rather than blanked: the reader keeps their place, and a rerun
         that changes nothing ends up looking like it changed nothing.
         It sticks to the top of the results block because a rerun is triggered
         from Options or a key column while the reader may be deep in a long
         diff — a loader at the top of a 3,000-row ledger is a loader nobody
         sees. Sticky is right HERE and wrong on the Crux (REL-081): this is
         one short line, not a panel. (No backticks in this file — the whole
         component is one template literal.) -->
    <div class="compare-loader" id="compareLoader" role="status" hidden>
      <span class="compare-spinner" aria-hidden="true"></span>
      <span class="compare-loader-text">
        <b id="compareLoaderMsg">Comparing…</b>
        <span class="note" id="compareLoaderNote"></span>
      </span>
    </div>
    <!-- The Crux is the CONTROL PANEL for the comparison: what was matched, the
         counts, and the way through them. It carries no findings list — the diff
         below is where a change is read, and a row of truncated one-line excerpts
         above it was a second, worse copy of the same information. The saved HTML
         report still lists every finding, because a static report has no
         navigation and no highlighted current row to carry them. -->
    <div class="crux" id="cruxPanel">
      <div class="crux-head">
        <span class="crux-title">&Delta; THE CRUX</span>
        <!-- What was matched against what. A reader cannot judge a row-level
             finding without it, and the duplicate-key fallback has to say so. -->
        <span class="crux-sub" id="cruxSub"></span>
      </div>
      <!-- The counts ARE the controls: a leading card for every place to
           review, then one per category. summary.js owns their text; #summary
           keeps its id so animateCounts, the QA harness and the HTML export
           are untouched. -->
      <div class="summary" id="summary"></div>
      <div class="crux-bar">
        <button type="button" class="crux-clear" id="cruxShowAll" hidden>Show all changes</button>
        <!-- The stepper moves WITHIN the selected card. It states the position
             in places ("Location 1 of 4") while the card keeps stating the
             semantic total ("2 records modified") — two units, both printed,
             never conflated (G80). aria-live announces the move without taking
             focus off the control that caused it. -->
        <div class="crux-nav" id="cruxNav" role="group" aria-label="Move through the changes" hidden>
          <button type="button" class="nav-step" id="navPrev" aria-label="Previous (p)" title="Previous (p)">&larr; Previous</button>
          <span class="nav-pos" id="navPos" aria-live="polite"></span>
          <button type="button" class="nav-step" id="navNext" aria-label="Next (n)" title="Next (n)">Next &rarr;</button>
        </div>
      </div>
    </div>
    <!-- Change Analysis (spec §8.7): an OUTPUT, not a mode — no tab, no second
         input, no button of its own. Placed immediately AFTER The Crux, which
         deliberately revises §8.7's "above Crux": the concrete changes are what a
         reader came for, and the analysis supports them. Still adjacent, so it is
         found without hunting. A <details> so the collapse is the browser's own
         keyboard behaviour — the whole header is the summary, and the chevron is
         sized to be seen. -->
    <details class="analysis" id="analysisPanel" open hidden>
      <summary class="an-head">
        <svg class="an-chev" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5.5 3L11 8l-5.5 5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <span class="an-title">CHANGE ANALYSIS</span><span class="an-lead" id="analysisLead"></span>
      </summary>
      <div class="an-body" id="analysisBody"></div>
    </details>
    <div class="view-bar" id="viewBar">
      <div class="seg" id="viewSeg" role="group" aria-label="Diff layout">
        <button data-view="sxs" aria-pressed="true">Side-by-side</button>
        <button data-view="unified" aria-pressed="false">Unified</button>
        <button data-view="raw" aria-pressed="false">Raw</button>
      </div>
      <div class="seg" id="scopeSeg" role="group" aria-label="Rows shown" style="display:none">
        <button data-scope="changed" aria-pressed="true">Changed only</button>
        <button data-scope="all" aria-pressed="false">All rows</button>
      </div>
      <button class="chip-toggle" id="wrapBtn" aria-pressed="true" title="Wrap long lines so the two panes stay aligned">Wrap</button>
      <button class="chip-toggle" id="expandJsonBtn" aria-pressed="true" title="Expand JSON for viewing without changing your inputs" hidden>Expand JSON</button>
      <span class="seg-hint" id="segHint">semantic highlighting stays on in every layout</span>
      <div class="view-actions">
        <button class="ghost-btn" id="shareBtn" aria-haspopup="dialog" aria-expanded="false">&#10697; Share</button>
        <button class="ghost-btn" id="exportBtn" aria-haspopup="dialog" aria-expanded="false">&darr; Download</button>
        <div class="pop" id="sharePop" role="dialog" aria-label="Share options">
          <h5>Share this diff</h5>
          <div class="share-mode"><b>Instant link</b><div class="note">whole diff compressed into the URL fragment — nothing stored on any server. Long by design; it pastes as a titled link where formatting is supported.</div></div>
          <button class="cta" id="copyLinkBtn">Copy link</button>
        </div>
        <div class="pop" id="exportPop" role="dialog" aria-label="Download options">
          <h5>Download</h5>
          <button class="exp-item" data-exp="json">Change list (.json)</button>
          <button class="exp-item" data-exp="patch">Unified patch (.diff)</button>
          <button class="exp-item" data-exp="html">HTML report</button>
          <!-- Scope stays visible: the HTML report is the one file whose contents
               depend on a control somewhere else on screen, and a reader who has
               "Changed only" selected should not have to guess. -->
          <p class="note">Written in this tab — nothing uploaded. Where a row scope
            is shown above, the HTML report follows it; the change list and the
            patch always contain every change.</p>
        </div>
      </div>
    </div>
    <div class="diff-card">
      <div class="diff-body" id="diffBody"></div>
      <svg id="ribbon" aria-hidden="true"></svg>
    </div>
  </section>`;
}
